/**
 * Environmental Geotech — Enforcement Actions  (Step 8a of the 8-step intelligence loop)
 *
 * Log and dispatch enforcement actions for confirmed environmental violations.
 * Actions include: authority notification, cease-and-desist order, legal referral,
 * on-site patrol, and remediation order (replant / restore).
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Scale, Users, ShieldAlert, ExternalLink,
  RefreshCw, ChevronDown, ChevronUp, TreePine,
} from 'lucide-react';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Spinner from '../../components/common/Spinner';
import Modal from '../../components/common/Modal';
import Alert from '../../components/common/Alert';
import api from '../../services/api';

// ─── Types ────────────────────────────────────────────────────────────────────

interface IncidentMission {
  id: string;
  name: string;
  status: string;
  updated_at: string;
  metadata?: {
    step7_status?: string;
    step8_status?: string;
    zone_name?: string;
    diagnosis?: string;
    severity?: string;
    canopy_loss_ha?: number;
    vertical?: string;
    [key: string]: any;
  };
}

interface EnforcementForm {
  action_type:       string;
  authority_name:    string;
  reference_no:      string;
  deadline_days:     string;
  notes:             string;
}

const EMPTY_FORM: EnforcementForm = {
  action_type:       'notify_authority',
  authority_name:    '',
  reference_no:      '',
  deadline_days:     '30',
  notes:             '',
};

const ACTION_TYPES = [
  { value: 'notify_authority',    label: 'Notify Environmental Authority' },
  { value: 'cease_desist',        label: 'Issue Cease-and-Desist Order' },
  { value: 'remediation_order',   label: 'Remediation Order (Replant / Restore)' },
  { value: 'on_site_patrol',      label: 'Dispatch On-Site Patrol' },
  { value: 'legal_referral',      label: 'Refer to Legal / Prosecutor' },
  { value: 'fine_assessment',     label: 'Assess Environmental Fine' },
];

const STEP7_STATUS_STYLE: Record<string, string> = {
  confirmed_critical:    'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  confirmed_enforcement: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  confirmed_monitor:     'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
};

const STEP7_STATUS_LABEL: Record<string, string> = {
  confirmed_critical:    'Critical',
  confirmed_enforcement: 'Enforcement',
  confirmed_monitor:     'Monitor',
};

// ─── Component ────────────────────────────────────────────────────────────────

export const EnforcementActionsPage: React.FC = () => {
  const [missions, setMissions]     = useState<IncidentMission[]>([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState<string | null>(null);
  const [success, setSuccess]       = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [actionId, setActionId]     = useState<string | null>(null);
  const [form, setForm]             = useState<EnforcementForm>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/missions?mission_type=environmental_survey&limit=50');
      const all: IncidentMission[] = res.data?.missions ?? [];
      setMissions(
        all.filter(
          (m) =>
            m.metadata?.vertical === 'environmental' &&
            (m.metadata?.step7_status === 'confirmed_critical' ||
             m.metadata?.step7_status === 'confirmed_enforcement' ||
             m.metadata?.step7_status === 'confirmed_monitor') &&
            !m.metadata?.step8_status
        )
      );
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Failed to load incidents');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDispatch = async (mission: IncidentMission) => {
    if (!form.authority_name.trim()) { setError('Authority / officer name is required'); return; }
    setSubmitting(true); setError(null);
    try {
      await api.patch(`/api/missions/${mission.id}`, {
        metadata: {
          ...mission.metadata,
          step8_status:          'dispatched',
          step8_action_type:     form.action_type,
          step8_authority:       form.authority_name.trim(),
          step8_reference:       form.reference_no.trim(),
          step8_deadline_days:   Number(form.deadline_days),
          step8_notes:           form.notes.trim(),
          step8_dispatched_at:   new Date().toISOString(),
        },
      });
      setSuccess(`Enforcement action dispatched for "${mission.name}"`);
      setActionId(null);
      setForm(EMPTY_FORM);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Failed to dispatch enforcement action');
    } finally {
      setSubmitting(false);
    }
  };

  const actionMission = missions.find((m) => m.id === actionId) ?? null;

  const INPUT_CLS  = 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';
  const SELECT_CLS = INPUT_CLS;

  return (
    <div className="space-y-6">

      {/* ── Header ── */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Scale className="w-7 h-7 text-green-700" /> Enforcement Actions
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Step 8 — Dispatch enforcement: authority notification, cease-and-desist, remediation orders
          </p>
        </div>
        <Button variant="outline" onClick={load}><RefreshCw className="w-4 h-4 mr-1" /> Refresh</Button>
      </div>

      {error   && <Alert type="error"   onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert type="success" onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* ── Incident list ── */}
      <Card padding="none">
        <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700">
          <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
            {missions.length} confirmed incident{missions.length !== 1 ? 's' : ''} awaiting enforcement
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-14"><Spinner size="lg" /></div>
        ) : missions.length === 0 ? (
          <div className="text-center py-14">
            <ShieldAlert className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">No confirmed incidents pending enforcement</p>
            <p className="text-sm text-gray-400 mt-1">Confirm findings in <strong>Analysis Review → Step 7</strong> first</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-gray-800">
            {missions.map((m) => {
              const isExpanded = expandedId === m.id;
              const statusKey  = m.metadata?.step7_status ?? '';
              return (
                <div key={m.id}>
                  <button
                    className="w-full flex items-center gap-4 px-5 py-4 hover:bg-gray-50 dark:hover:bg-gray-700/30 text-left"
                    onClick={() => setExpandedId(isExpanded ? null : m.id)}
                  >
                    <TreePine className="w-4 h-4 flex-shrink-0 text-green-600" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{m.name}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{m.metadata?.zone_name ?? '—'}</p>
                    </div>
                    {statusKey && (
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STEP7_STATUS_STYLE[statusKey] ?? 'bg-gray-100 text-gray-700'}`}>
                        {STEP7_STATUS_LABEL[statusKey] ?? statusKey}
                      </span>
                    )}
                    {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0 rotate-180" /> : <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />}
                  </button>

                  {isExpanded && (
                    <div className="px-5 pb-5 pt-2 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-100 dark:border-gray-700 space-y-3">
                      {m.metadata?.diagnosis && (
                        <p className="text-sm text-gray-700 dark:text-gray-300">{m.metadata.diagnosis}</p>
                      )}
                      {m.metadata?.canopy_loss_ha != null && (
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Canopy loss: <strong className="text-red-600 dark:text-red-400">
                            {Number(m.metadata.canopy_loss_ha).toFixed(1)} ha
                          </strong>
                        </p>
                      )}
                      <Button size="sm" onClick={() => { setActionId(m.id); setForm(EMPTY_FORM); }}>
                        <Scale className="w-4 h-4 mr-1" /> Plan Enforcement Action
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* ── Enforcement action modal ── */}
      {actionMission && (
        <Modal onClose={() => setActionId(null)} panelClassName="max-w-lg p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">Enforcement Action</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{actionMission.name}</p>
              </div>
              <Scale className="w-5 h-5 text-green-700" />
            </div>

            <div className="space-y-3 mb-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Action Type</label>
                <select className={SELECT_CLS} value={form.action_type}
                  onChange={(e) => setForm((p) => ({ ...p, action_type: e.target.value }))}>
                  {ACTION_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Authority / Officer Name *</label>
                <input className={INPUT_CLS} placeholder="e.g. Department of Environment, Mr. Ahmed"
                  value={form.authority_name}
                  onChange={(e) => setForm((p) => ({ ...p, authority_name: e.target.value }))} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Response Deadline (days)</label>
                <input className={INPUT_CLS} type="number" min="1" max="365"
                  value={form.deadline_days}
                  onChange={(e) => setForm((p) => ({ ...p, deadline_days: e.target.value }))} />
              </div>

              <div className="rounded-lg bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800 p-3 space-y-2">
                <p className="text-xs font-semibold text-green-700 dark:text-green-400 flex items-center gap-1">
                  <ExternalLink className="w-3.5 h-3.5" /> Case / Notice Reference (optional)
                </p>
                <input className={INPUT_CLS} placeholder="e.g. ENV-ENFC-2026-0018"
                  value={form.reference_no}
                  onChange={(e) => setForm((p) => ({ ...p, reference_no: e.target.value }))} />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Notes</label>
                <textarea rows={2} className={INPUT_CLS}
                  placeholder="Legal jurisdiction, parcel co-ordinates, access conditions, precedent cases…"
                  value={form.notes} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} />
              </div>
            </div>

            <div className="flex gap-3">
              <Button onClick={() => handleDispatch(actionMission)} loading={submitting}
                disabled={!form.authority_name.trim() || submitting} className="flex-1">
                <Users className="w-4 h-4 mr-1" /> Dispatch Action
              </Button>
              <Button variant="outline" onClick={() => setActionId(null)} disabled={submitting} className="flex-1">
                Cancel
              </Button>
            </div>
        </Modal>
      )}
    </div>
  );
};

export default EnforcementActionsPage;

// Made with Bob
